import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Tab,
  Tabs,
  Tooltip,
} from "react-bootstrap";
import {
  MdCalendarToday,
  MdEdit,
  MdExpandLess,
  MdExpandMore,
  MdOutlineCancel,
  MdOutlineCheckCircleOutline,
  MdOutlineQueryBuilder,
  MdUploadFile,
  MdVisibility,
} from "react-icons/md";
import { useHistory, useParams } from "react-router-dom";
import { RequestDetailsProps } from "../../@types/requests";
import Loading from "../../Components/Loading";
import Status from "../../Components/Status";
import { getRequestForId, SignatureVerificationProps, verificarAssinaturas } from "../../services/requests";
import { getTramiteOrgan } from "../../services/flow";
import { cpfMask } from "../../utils/cpfFormat";
import dateFormat from "../../utils/dateFormat";
import StatusRequest from "../Requests/Components/StatusRequest";
import {
  AssignmentIcon,
  BoxBodyCard,
  BoxBodyCardTable,
  BoxButtonsAttachments,
  BoxHeaderCard,
  BoxTextsTitleCard,
  BoxTimeLine,
  ButtonAttachment,
  ButtonReturn,
  Card,
  CardFooterCustom,
  ContainerMain,
  CustomTable,
  InfoIcon,
  SubTextMark,
  TextMark,
  TextsTitleCard,
  TimeLine,
  TimeLineContainer,
  TimeLineContent,
  TimeLineContentBoxText,
  TimeSubTitle,
  TimeTitle,
  TitleHeadCard,
} from "./styles";
import { useStateValue } from "../../providers/StateProvider";
import { RiAlertFill } from "react-icons/ri";


type Params = {
  identificador: string;
};

const RequestDetails = (): JSX.Element => {
  const downloadLink = useRef(null);
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(true);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const history = useHistory();
  const [countAllDocumentUnreaded, setCountAllDocumentUnreaded] =
    React.useState<number>(0);
  const { identificador } = useParams<Params>();
  const [loading, setLoading] = React.useState(false);
  const [requestData, setRequestData] = React.useState<
    RequestDetailsProps | undefined
  >(undefined);
  const [showDetails, setShowDetails] = React.useState<string>("");
  const [showModalDocReceived, setShowModalDocReceived] =
    React.useState<boolean>(false);
  const [docReceivedSelected, setDocReceivedSelected] = React.useState<{
    id: string;
    nome: string;
    url: string;
  }>();
  const [showModalAnexos, setShowModalAnexos] = React.useState<boolean>(false);
  const [anexosReceived, setAnexosReceivedSelected] = React.useState<{
    id: string;
    arquivo: string;
  }>();
  const [signatureData, setSignatureData] = useState<Record<string, SignatureVerificationProps>>({});
  const [isLastStep, setIsLastStep] = useState<boolean>(false);
  const [{ user }] = useStateValue();

  const handleNew = (): void => {
    history.push(`/solicitacoes/cadastra-tramite/${identificador}`);
  };
  const handleAttachDocument = (): void => {
    history.push(
      `/solicitacoes/${identificador}/${requestData.id}/anexar-documento/`
    );
  };
  const handleEditDocument = (
    id: number,
    id_orgao: number,
    identificador: string,
    id_documento: string
  ): void => {
    history.push(
      `/solicitacoes/${id}/${id_orgao}/documento/${identificador}/editar-documento/${id_documento}`
    );
  };

  const handleShowDocument = (
    id: string,
    id_orgao: number,
    id_document: string
  ): void => {
    history.push(
      `/solicitacoes/${id}/${id_orgao}/documento/${id_document}/visualizar`
    );
  };

  const handleShowDocReceived = (doc: {
    id: string;
    nome: string;
    url: string;
  }): void => {
    setDocReceivedSelected(doc);
    setShowModalDocReceived(true);
  };

  useEffect(() => {
    if (document.body.style.overflow === "hidden") {
      document.body.style.overflow = "";
    }
  }, []);

  React.useEffect(() => {
    const getDetailsRequest = async () => {
      try {
        setLoading(true);
        const { data } = await getRequestForId(identificador);
        if (data?.documentos_solicitacao) {
          let count = 0;
          data.documentos_solicitacao.forEach((doc: any) => {
            if (doc.verificado_em === null) {
              count += 1;
            }
          });
          setCountAllDocumentUnreaded(count);
        }
        setRequestData(data);

        if (data?.documentos_resposta) {
          const signatures: Record<string, SignatureVerificationProps> = {};
          await Promise.all(
            data.documentos_resposta.map(async (doc) => {
              try {
                const response = await verificarAssinaturas(doc.id);
                signatures[doc.id] = response.data;
              } catch (error) {
                console.error(`Erro ao verificar assinaturas para o documento ${doc.id}:`, error);
                signatures[doc.id] = {
                  remaining_quantity: 0,
                  remaining_signatures: {},
                  tramitation: false,
                  signatures: [],
                };
              }
            })
          );
          setSignatureData(signatures);
        }

        try {
          const token: string | null = localStorage.getItem("gov_access_token");
          const tramiteData = await getTramiteOrgan(identificador, token);
          setIsLastStep(tramiteData?.data?.status === "Finalizado");
        } catch (error) {
          console.error("Erro ao verificar etapa de tramitação:", error);
          setIsLastStep(false);
        }
      } catch (error) {
        console.error("Erro ao buscar detalhes da solicitação:", error);
      } finally {
        setLoading(false);
      }
    };
    getDetailsRequest();
  }, [identificador]);

  const handleIframeLoad = () => {
    setLoadingAttachments(false);
  };

  const handleIframeError = () => {
    setIsDocumentLoaded(false);
    setLoadingAttachments(false);
  };

  const isImage = (url) => {
    return url && /\.(jpg|jpeg|png|sdc)$/i.test(url);
  };

  const hasPendingSignatures = useMemo(() => {
    return Object.values(signatureData).some((sig) => sig.remaining_quantity > 0);
  }, [signatureData]);

  const getTotalPendingSignatures = useMemo(() => {
    return Object.values(signatureData).reduce(
      (total, sig) => total + sig.remaining_quantity,
      0
    );
  }, [signatureData]);

  const isOtherAttendant = useMemo(() => {
    return (
      (requestData?.status === "Em Andamento" ||
        requestData?.status === "Restituído") &&
      user?.cpf !== requestData?.atendente?.atendente
    );
  }, [requestData, user]);


  return (
    <ContainerMain>
      <Row>
        <Col sm={12} md={5}>
          <Card>
            <BoxHeaderCard>
              <TitleHeadCard>
                <InfoIcon />
                <BoxTextsTitleCard>
                  <TextsTitleCard>Informações da solicitação</TextsTitleCard>
                </BoxTextsTitleCard>
              </TitleHeadCard>
            </BoxHeaderCard>
            {loading ? (
              <Loading />
            ) : (
              <BoxBodyCard>
                <TextMark>Protocolo</TextMark>
                <SubTextMark>
                  {requestData?.protocolo || "Sem informação"}
                </SubTextMark>

                <TextMark>Hash</TextMark>
                <SubTextMark>
                  {requestData?.hash || "Sem informação"}
                </SubTextMark>

                <TextMark>Identificador</TextMark>
                <SubTextMark>
                  {requestData?.identificador || "Sem informação"}
                </SubTextMark>

                <TextMark>CPF</TextMark>
                <SubTextMark>{cpfMask(requestData?.cpf || "")}</SubTextMark>

                <TextMark>Fluxo</TextMark>
                <SubTextMark>
                  {requestData?.flow?.titulo || "Sem informação"}
                </SubTextMark>

                <TextMark>Assinado pelo cidadão</TextMark>
                <Status type={requestData?.assinado?.toString() || "false"} />
              </BoxBodyCard>
            )}
          </Card>
        </Col>
        <Col sm={12} md={7}>
          <Card>
            <BoxHeaderCard>
              <TitleHeadCard>
                <InfoIcon />
                <BoxTextsTitleCard>
                  <TextsTitleCard>Tramitação</TextsTitleCard>
                </BoxTextsTitleCard>
              </TitleHeadCard>
              {(requestData?.status === "Aguardando" ||
                requestData?.status === "Solicitado" ||
                requestData?.status === "Encaminhado" ||
                (requestData?.status === "Em Andamento" &&
                  user?.cpf === requestData?.atendente?.atendente)) && (
                  <>
                    <Button
                      variant="primary"
                      onClick={handleNew}
                      disabled={
                        isOtherAttendant ||
                        !!countAllDocumentUnreaded ||
                        (isLastStep && hasPendingSignatures)
                      }
                    >
                      <MdEdit /> Tramitar
                    </Button>
                  </>
                )}
            </BoxHeaderCard>
            {!!countAllDocumentUnreaded && (
              <Alert
                variant="primary"
                style={{ marginLeft: 5, marginRight: 5 }}
              >
                {countAllDocumentUnreaded > 1
                  ? `Existem ${countAllDocumentUnreaded} documentos da solicitação para serem verificados antes da tramitação`
                  : ` Existe ${countAllDocumentUnreaded} documento da solicitação para ser verificado antes da tramitação`}
              </Alert>
            )}
            {hasPendingSignatures && isLastStep && (
              <Alert variant="warning" style={{ marginLeft: 5, marginRight: 5 }}>
                <RiAlertFill size={18} /> <b>Atenção!</b> O documento de resposta possui assinaturas pendentes. Esta solicitação só pode ser finalizada após a conclusão de todas as assinaturas.
              </Alert>
            )}
            {loading ? (
              <Loading />
            ) : (
              <BoxBodyCard
                style={countAllDocumentUnreaded ? { minHeight: "476px" } : {}}
              >
                <Row>
                  <Col>
                    <TextMark>Órgão</TextMark>
                    <SubTextMark>
                      {requestData?.orgao?.nome || "Sem informação"}
                    </SubTextMark>
                  </Col>
                  <Col>
                    <TextMark>Setor</TextMark>
                    <SubTextMark>
                      {requestData?.setor?.nome || "Sem informação"}
                    </SubTextMark>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TextMark>Atendente atual</TextMark>
                    <SubTextMark>
                      {requestData?.atendente?.nome || "Sem informação"}
                    </SubTextMark>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TextMark>Atendente que assinou</TextMark>
                    <SubTextMark>
                      {requestData?.nome_atendente_assinado || "Sem informação"}
                    </SubTextMark>
                  </Col>
                  <Col>
                    <TextMark>Hash de assinatura do atendente</TextMark>
                    <SubTextMark>
                      {requestData?.hash_atendente || "Sem informação"}
                    </SubTextMark>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TextMark>Descrição</TextMark>
                    <SubTextMark
                      dangerouslySetInnerHTML={{
                        __html: requestData?.descricao || "Sem informação",
                      }}
                    />
                  </Col>
                  <Col>
                    <TextMark>Status</TextMark>
                    <SubTextMark>
                      {requestData?.status || "Sem informação"}
                    </SubTextMark>
                  </Col>
                </Row>
              </BoxBodyCard>
            )}
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <BoxHeaderCard>
              <TitleHeadCard>
                <InfoIcon />
                <BoxTextsTitleCard>
                  <TextsTitleCard>Outras Informações</TextsTitleCard>
                </BoxTextsTitleCard>
              </TitleHeadCard>
            </BoxHeaderCard>
            <BoxBodyCardTable>
              <Tabs defaultActiveKey="history" className="mx-4">
                <Tab eventKey="history" title="Histórico">
                  <CustomTable striped responsive>
                    <thead className="table-header">
                      <tr>
                        <th>Ordem</th>
                        <th>Status</th>
                        <th>Identificador</th>
                        <th>Tramitado em</th>
                        <th>Atendente</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {loading ? (
                        <tr className="text-center">
                          <td colSpan={6}>
                            <Loading />
                          </td>
                        </tr>
                      ) : (
                        <>
                          {requestData?.historico.length ? (
                            requestData?.historico?.map((item, index) => (
                              <Fragment key={item.identificador}>
                                <tr>
                                  <td>#{index + 1}</td>
                                  <td>
                                    <StatusRequest type={item.status} />
                                  </td>
                                  <td>{item.identificador}</td>
                                  <td>{dateFormat(item.updated_at, true)}</td>
                                  <td>
                                    {item.assinado_por === "Atendente"
                                      ? "Não consta"
                                      : item.assinado_por}
                                  </td>
                                  <td className="text-right">
                                    <Button
                                      variant="outline-secondary"
                                      onClick={() => {
                                        showDetails &&
                                          showDetails === item.identificador
                                          ? setShowDetails("")
                                          : setShowDetails(item.identificador);
                                      }}
                                    >
                                      {showDetails &&
                                        showDetails === item.identificador ? (
                                        <MdExpandMore />
                                      ) : (
                                        <MdExpandLess />
                                      )}
                                    </Button>
                                  </td>
                                </tr>
                                {showDetails &&
                                  showDetails === item.identificador && (
                                    <>
                                      <tr className="hiddenDetails">
                                        <th colSpan={3}>DESCRIÇÃO</th>
                                        <td
                                          colSpan={3}
                                          className="text-right"
                                          dangerouslySetInnerHTML={{
                                            __html: item?.descricao,
                                          }}
                                        ></td>
                                      </tr>
                                      <tr className="hiddenDetails">
                                        <th colSpan={3}>COMENTÁRIO INTERNO</th>
                                        <td
                                          colSpan={3}
                                          className="text-right"
                                          dangerouslySetInnerHTML={{
                                            __html:
                                              item?.comentario_interno ||
                                              "Não informado",
                                          }}
                                        ></td>
                                      </tr>
                                      <tr className="hiddenDetails">
                                        <th colSpan={3}>ÓRGÃO</th>
                                        <td colSpan={3} className="text-right">
                                          {item.orgao}
                                        </td>
                                      </tr>
                                      <tr className="hiddenDetails">
                                        <th colSpan={3}>HASH</th>
                                        <td colSpan={3} className="text-right">
                                          {item.hash}
                                        </td>
                                      </tr>
                                      <tr className="hiddenDetails">
                                        <th colSpan={3}>ASSINADO POR</th>
                                        <td colSpan={3} className="text-right">
                                          {item.assinado_por === "Atendente"
                                            ? "Não consta"
                                            : item.assinado_por}
                                        </td>
                                      </tr>
                                    </>
                                  )}
                              </Fragment>
                            ))
                          ) : (
                            <tr className="text-center">
                              <td colSpan={6}>Nenhum histórico encontrado</td>
                            </tr>
                          )}{" "}
                        </>
                      )}
                    </tbody>
                  </CustomTable>
                </Tab>
                <Tab
                  eventKey="doc_request"
                  title={
                    <>
                      Documentos da solicitação{" "}
                      {countAllDocumentUnreaded > 0 && (
                        <OverlayTrigger
                          placement="right"
                          delay={{ show: 250, hide: 400 }}
                          overlay={
                            <Tooltip id="button-tooltip">
                              {countAllDocumentUnreaded > 1
                                ? `Documentos não verificados: ${countAllDocumentUnreaded}`
                                : `Documento não verificado: ${countAllDocumentUnreaded}`}
                            </Tooltip>
                          }
                        >
                          <Badge bg="primary">{countAllDocumentUnreaded}</Badge>
                        </OverlayTrigger>
                      )}
                    </>
                  }
                >
                  <CustomTable striped responsive>
                    <thead className="table-header">
                      <tr>
                        <th>Ordem</th>
                        <th>Título</th>
                        <th>Conformidade</th>
                        <th>Verificado em</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {loading ? (
                        <tr className="text-center">
                          <td colSpan={5}>
                            <Loading />
                          </td>
                        </tr>
                      ) : (
                        <>
                          {" "}
                          {requestData?.documentos_solicitacao.length ? (
                            requestData?.documentos_solicitacao?.map(
                              (item, index) => (
                                <Fragment key={item.id}>
                                  <tr>
                                    <td>#{index + 1}</td>
                                    <td>{item.titulo}</td>
                                    <td>
                                      <Status
                                        type={item?.conformidade.toString()}
                                      />
                                    </td>
                                    <td>
                                      {item.verificado_em
                                        ? dateFormat(item.verificado_em, true)
                                        : "Sem informação"}
                                    </td>
                                    <td className="text-right">
                                      <Button
                                        variant="outline-secondary mx-2"
                                        onClick={() =>
                                          history.push(
                                            `/solicitacoes/documento/${item?.id}/${requestData.identificador}/visualizar-documentos`
                                          )
                                        }
                                      >
                                        <MdVisibility size={22} />
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        onClick={() => {
                                          showDetails && showDetails === item.id
                                            ? setShowDetails("")
                                            : setShowDetails(item.id);
                                        }}
                                      >
                                        {showDetails &&
                                          showDetails === item.id ? (
                                          <MdExpandMore />
                                        ) : (
                                          <MdExpandLess />
                                        )}
                                      </Button>
                                    </td>
                                  </tr>
                                  {showDetails && showDetails === item.id && (
                                    <>
                                      <tr className="hiddenDetails">
                                        <th colSpan={3}>VERIFICADO POR</th>
                                        <td colSpan={2} className="text-right">
                                          {!item.verificado_por &&
                                            "Não informado"}
                                        </td>
                                      </tr>
                                      {item.verificado_por && (
                                        <td
                                          colSpan={2}
                                          style={{ padding: "15px 30px" }}
                                        >
                                          <>
                                            <div>
                                              Nome: {item.verificado_por?.nome}{" "}
                                            </div>
                                            <div>
                                              CPF: {item.verificado_por?.cpf}{" "}
                                            </div>
                                            <div>
                                              Data da Verificação:
                                              {new Date(
                                                item?.verificado_em
                                              ).toLocaleDateString("pt-BR")}
                                            </div>
                                          </>
                                        </td>
                                      )}
                                    </>
                                  )}
                                </Fragment>
                              )
                            )
                          ) : (
                            <tr className="text-center">
                              <td colSpan={5}>Nenhum histórico encontrado</td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </CustomTable>
                </Tab>
                <Tab

                  eventKey="doc_response"
                  title={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: '10px',
                        justifyContent: "center",
                      }}>
                      Documentos de Resposta{" "}
                      {getTotalPendingSignatures > 0 && (
                        <p
                          style={{
                            margin: 0,
                            backgroundColor: '#fff3cd',
                            color: '#664d03',
                            borderRadius: '100%',
                            width: '25px',
                            height: '25px'

                          }}
                        >{getTotalPendingSignatures}</p>
                      )}
                    </div>
                  }
                >
                  <CustomTable striped responsive>
                    <thead className="table-header">
                      <tr>
                        <th>Ordem</th>
                        <th>Título</th>
                        <th>Assinado</th>
                        <th>Criado em</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {loading ? (
                        <tr className="text-center">
                          <td colSpan={5}>
                            <Loading />
                          </td>
                        </tr>
                      ) : (
                        <>
                          {requestData?.documentos_resposta.length ? (
                            requestData?.documentos_resposta?.map((item, index) => {
                              const sigData = signatureData[item.id];

                              return (
                                <Fragment key={item.id}>
                                  <tr>
                                    <td>#{index + 1}</td>
                                    <td>{item.titulo}</td>
                                    <td>
                                      {sigData?.tramitation ? (
                                        <MdOutlineCheckCircleOutline size={20} color="green" />
                                      ) : (
                                        <MdOutlineCancel size={20} color="red" />
                                      )}
                                    </td>
                                    <td>{dateFormat(item.created_at, true)}</td>
                                    <td className="text-right">
                                      {!isOtherAttendant && !sigData?.tramitation && (
                                        <Button
                                          variant="outline-secondary mx-2"
                                          onClick={() =>
                                            handleEditDocument(
                                              requestData?.id,
                                              requestData?.orgao.id,
                                              requestData?.identificador,
                                              item.id.toString()
                                            )
                                          }
                                        >
                                          <MdEdit size={22} />
                                          Assinar
                                        </Button>
                                      )}
                                      <Button
                                        variant="outline-secondary mx-2"
                                        onClick={() =>
                                          handleShowDocument(
                                            requestData?.identificador,
                                            requestData?.orgao.id,
                                            item.id
                                          )
                                        }
                                      >
                                        <MdVisibility size={22} />
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        onClick={() => {
                                          showDetails && showDetails === item.id
                                            ? setShowDetails("")
                                            : setShowDetails(item.id);
                                        }}
                                      >
                                        {showDetails && showDetails === item.id ? (
                                          <MdExpandMore />
                                        ) : (
                                          <MdExpandLess />
                                        )}
                                      </Button>
                                    </td>
                                  </tr>
                                  {showDetails && showDetails === item.id && (
                                    <>
                                      <tr className="hiddenDetails">
                                        <th colSpan={5} style={{ fontWeight: "bold", padding: "15px 30px" }}>
                                          Informações de Assinatura
                                        </th>
                                      </tr>
                                      <tr className="hiddenDetails">
                                        <td colSpan={5} style={{ padding: "15px 30px" }}>
                                          <div style={{ marginBottom: "20px" }}>
                                            <h6
                                              style={{
                                                textAlign: "left",
                                                color: "#6c757d",
                                                marginBottom: "15px",
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                              }}
                                            >
                                              ASSINATURAS CONCLUÍDAS
                                            </h6>
                                            {sigData?.signatures ? (
                                              Array.isArray(sigData.signatures) ? (
                                                sigData.signatures.length > 0 ? (
                                                  sigData.signatures
                                                    .filter((sig) => sig.hash !== null) 
                                                    .map((sig, index) => (
                                                      <div
                                                        key={sig.id || index}
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent: "space-between",
                                                          marginBottom: "10px",
                                                          backgroundColor: "#ffffff",
                                                          borderRadius: "5px",
                                                          padding: "10px",
                                                          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                        }}
                                                      >
                                                        <div style={{ display: "flex", alignItems: "center" }}>
                                                          <MdOutlineCheckCircleOutline
                                                            size={20}
                                                            color="green"
                                                            style={{ marginRight: "10px" }}
                                                          />
                                                          <div>
                                                            <div style={{ fontWeight: "bold", textAlign: "left" }}>
                                                              {sig.assinante.first_name || "Desconhecido"}
                                                            </div>
                                                            <div style={{ fontSize: "12px", color: "#6c757d", textAlign: "left" }}>
                                                              {sig.perfil || "Sem perfil"}
                                                            </div>
                                                          </div>
                                                        </div>
                                                        <div style={{ display: "flex", alignItems: "center" }}>
                                                          <MdCalendarToday
                                                            size={14}
                                                            color="#6c757d"
                                                            style={{ marginRight: "5px" }}
                                                          />
                                                          <span style={{ fontSize: "12px", color: "#6c757d" }}>
                                                            {sig.created_at
                                                              ? new Date(sig.created_at).toLocaleString("pt-BR")
                                                              : "Sem data"}
                                                          </span>
                                                        </div>
                                                      </div>
                                                    ))
                                                ) : (
                                                  <div
                                                    style={{
                                                      backgroundColor: "#ffffff",
                                                      borderRadius: "5px",
                                                      padding: "10px",
                                                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                    }}
                                                  >
                                                    <div style={{ fontSize: "14px", color: "#6c757d", textAlign: "left" }}>
                                                      Nenhuma assinatura concluída
                                                    </div>
                                                  </div>
                                                )
                                              ) : (
                                                sigData.signatures.hash !== null ? (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "space-between",
                                                      backgroundColor: "#ffffff",
                                                      borderRadius: "5px",
                                                      padding: "10px",
                                                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                    }}
                                                  >
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                      <MdOutlineCheckCircleOutline
                                                        size={20}
                                                        color="green"
                                                        style={{ marginRight: "10px" }}
                                                      />
                                                      <div>
                                                        <div style={{ fontWeight: "bold", textAlign: "left" }}>
                                                          {sigData.signatures.assinante.first_name || "Desconhecido"}
                                                        </div>
                                                        <div style={{ fontSize: "12px", color: "#6c757d", textAlign: "left" }}>
                                                          {sigData.signatures.perfil || "Sem perfil"}
                                                        </div>
                                                      </div>
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                      <MdCalendarToday
                                                        size={14}
                                                        color="#6c757d"
                                                        style={{ marginRight: "5px" }}
                                                      />
                                                      <span style={{ fontSize: "12px", color: "#6c757d" }}>
                                                        {sigData.signatures.created_at
                                                          ? new Date(sigData.signatures.created_at).toLocaleString("pt-BR")
                                                          : "Sem data"}
                                                      </span>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <div
                                                    style={{
                                                      backgroundColor: "#ffffff",
                                                      borderRadius: "5px",
                                                      padding: "10px",
                                                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                    }}
                                                  >
                                                    <div style={{ fontSize: "14px", color: "#6c757d", textAlign: "left" }}>
                                                      Nenhuma assinatura concluída
                                                    </div>
                                                  </div>
                                                )
                                              )
                                            ) : (
                                              <div
                                                style={{
                                                  backgroundColor: "#ffffff",
                                                  borderRadius: "5px",
                                                  padding: "10px",
                                                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                }}
                                              >
                                                <div style={{ fontSize: "14px", color: "#6c757d", textAlign: "left" }}>
                                                  Nenhuma assinatura concluída
                                                </div>
                                              </div>
                                            )}
                                          </div>

                                          {sigData?.remaining_quantity > 0 && (
                                            <div style={{ marginBottom: "20px" }}>
                                              <h6
                                                style={{
                                                  color: "#6c757d",
                                                  marginBottom: "15px",
                                                  fontSize: "14px",
                                                  fontWeight: "bold",
                                                  textAlign: "left",
                                                }}
                                              >
                                                ASSINATURAS PENDENTES
                                              </h6>
                                              {sigData.remaining_signatures &&
                                                Object.keys(sigData.remaining_signatures).length > 0 ? (
                                                Object.entries(sigData.remaining_signatures).map(([profile, quantity], index) =>
                                                  quantity > 0 ? (
                                                    Array.from({ length: quantity }).map((_, qIndex) => (
                                                      <div
                                                        key={`${index}-${qIndex}`}
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          marginBottom: "10px",
                                                          backgroundColor: "#ffffff",
                                                          borderRadius: "5px",
                                                          padding: "10px",
                                                          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                        }}
                                                      >
                                                        <div style={{ display: "flex", alignItems: "center" }}>
                                                          <MdOutlineQueryBuilder
                                                            size={20}
                                                            color="#FFC107"
                                                            style={{ marginRight: "10px" }}
                                                          />
                                                          <div>
                                                            <div style={{ fontWeight: "bold", textAlign: "left" }}>
                                                              Pendente
                                                            </div>
                                                            <div style={{ fontSize: "12px", color: "#6c757d", textAlign: "left" }}>
                                                              {profile}
                                                            </div>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    ))
                                                  ) : null
                                                )
                                              ) : (
                                                <div
                                                  style={{
                                                    backgroundColor: "#ffffff",
                                                    borderRadius: "5px",
                                                    padding: "10px",
                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                  }}
                                                >
                                                  <div style={{ fontSize: "14px", color: "#6c757d", textAlign: "left" }}>
                                                    Nenhum perfil pendente informado
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    </>
                                  )}
                                </Fragment>
                              )
                            })
                          ) : (
                            <tr className="text-center">
                              <td colSpan={5}>Nenhum histórico encontrado</td>
                            </tr>
                          )}{" "}
                        </>
                      )}
                    </tbody>
                  </CustomTable>
                </Tab>
                <Tab eventKey="docs_received" title="Anexos recebidos">
                  <CustomTable striped responsive>
                    <thead className="table-header">
                      <tr>
                        <th>Ordem</th>
                        <th>Nome</th>
                        <th>Hash</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {loading ? (
                        <tr className="text-center">
                          <td colSpan={5}>
                            <Loading />
                          </td>
                        </tr>
                      ) : (
                        <>
                          {" "}
                          {requestData?.arquivos_recebidos.length ? (
                            requestData?.arquivos_recebidos?.map(
                              // eslint-disable-next-line array-callback-return
                              (item, index) => {
                                if (item.id) {
                                  return (
                                    <Fragment key={item.id}>
                                      <tr>
                                        <td>#{index + 1}</td>
                                        <td>{item.nome}</td>
                                        <td>{item.id}</td>
                                        <td className="text-right">
                                          <Button
                                            variant="outline-secondary mx-2"
                                            onClick={() =>
                                              handleShowDocReceived(item)
                                            }
                                          >
                                            <MdVisibility size={22} />
                                          </Button>
                                        </td>
                                      </tr>
                                    </Fragment>
                                  );
                                }
                              }
                            )
                          ) : (
                            <tr className="text-center">
                              <td colSpan={5}>Nenhum histórico encontrado</td>
                            </tr>
                          )}{" "}
                        </>
                      )}
                    </tbody>
                  </CustomTable>
                </Tab>
                <Tab eventKey="attach_doc" title="Anexos de resposta">
                  {!isOtherAttendant && (
                    <BoxButtonsAttachments>
                      <ButtonAttachment
                        variant="primary"
                        onClick={handleAttachDocument}
                      >
                        <MdUploadFile size={20} /> Anexar documento
                      </ButtonAttachment>
                    </BoxButtonsAttachments>
                  )}
                  <CustomTable striped responsive>
                    <thead className="table-header">
                      <tr>
                        <th>Ordem</th>
                        <th>Título</th>
                        <th className="text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {loading ? (
                        <tr className="text-center">
                          <td colSpan={5}>
                            <Loading />
                          </td>
                        </tr>
                      ) : (
                        <>
                          {requestData?.anexo_documentos_resposta.length ? (
                            requestData?.anexo_documentos_resposta?.map(
                              (item, index) => (
                                <Fragment key={item.id}>
                                  <tr>
                                    <td>#{index + 1}</td>
                                    <td>{item.assunto}</td>
                                    <td className="text-right">
                                      <Button
                                        variant="outline-secondary mx-2"
                                        onClick={() => {
                                          handleShowDocReceived({
                                            id: item.id,
                                            nome: item.assunto,
                                            url: item.arquivo,
                                          });
                                        }}
                                      >
                                        <MdVisibility size={22} />
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        onClick={() => {
                                          showDetails && showDetails === item.id
                                            ? setShowDetails("")
                                            : setShowDetails(item.id);
                                        }}
                                      >
                                        {showDetails &&
                                          showDetails === item.id ? (
                                          <MdExpandMore />
                                        ) : (
                                          <MdExpandLess />
                                        )}
                                      </Button>
                                    </td>
                                  </tr>
                                  {showDetails && showDetails === item.id && (
                                    <>
                                      <tr className="hiddenDetails">
                                        <th colSpan={2}>Criado por:</th>
                                        <td className="text-right">
                                          {item.criado_por || "Não informado"}
                                        </td>
                                      </tr>
                                      <tr className="hiddenDetails">
                                        <th colSpan={3}>Descrição:</th>
                                      </tr>
                                      <tr>
                                        <td colSpan={3}>
                                          <div
                                            dangerouslySetInnerHTML={{
                                              __html: item.descricao,
                                            }}
                                          />
                                        </td>
                                      </tr>
                                    </>
                                  )}
                                </Fragment>
                              )
                            )
                          ) : (
                            <tr className="text-center">
                              <td colSpan={5}>Nenhum anexo encontrado</td>
                            </tr>
                          )}{" "}
                        </>
                      )}
                    </tbody>
                  </CustomTable>
                </Tab>
                {requestData?.restituicao?.length > 0 && (
                  <Tab eventKey="info" title="Informações complementares">
                    <CustomTable striped responsive>
                      <thead className="table-header">
                        <tr>
                          <th>Ordem</th>
                          <th>Resposta</th>
                          <th>Tramitado em</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {loading ? (
                          <tr className="text-center">
                            <td colSpan={5}>
                              <Loading />
                            </td>
                          </tr>
                        ) : (
                          <>
                            {" "}
                            {requestData?.restituicao?.length ? (
                              requestData?.restituicao?.map((item, index) => (
                                <Fragment key={item.id}>
                                  <tr>
                                    <td>#{index + 1}</td>
                                    <td>{item.resposta}</td>
                                    <td>{dateFormat(item.updated_at, true)}</td>
                                    <td className="text-right">
                                      <Button
                                        variant="outline-secondary"
                                        onClick={() => {
                                          showDetails && showDetails === item.id
                                            ? setShowDetails("")
                                            : setShowDetails(item.id);
                                        }}
                                      >
                                        {showDetails &&
                                          showDetails === item.id ? (
                                          <MdExpandMore />
                                        ) : (
                                          <MdExpandLess />
                                        )}
                                      </Button>
                                    </td>
                                  </tr>
                                  {showDetails && showDetails === item.id && (
                                    <>
                                      {item.anexos?.length > 0 ? (
                                        <>
                                          {item.anexos.map((anexo, ind) => (
                                            <tr>
                                              <td>
                                                #{index + 1}.{ind + 1}
                                              </td>
                                              <td>{`${ind + 1} - Anexo`}</td>
                                              <td></td>
                                              <td className="text-right">
                                                <Button
                                                  variant="outline-secondary"
                                                  onClick={() => {
                                                    handleShowDocReceived({
                                                      id: `${ind}-${anexo.id}`,
                                                      nome: `Anexo - ${ind + 1
                                                        }`,
                                                      url: anexo.arquivo,
                                                    });
                                                  }}
                                                >
                                                  <MdVisibility size={22} />
                                                </Button>
                                              </td>
                                            </tr>
                                          ))}
                                        </>
                                      ) : (
                                        <tr className="text-center">
                                          <td colSpan={5}>
                                            Nenhum anexo encontrado.
                                          </td>
                                        </tr>
                                      )}
                                    </>
                                  )}
                                </Fragment>
                              ))
                            ) : (
                              <tr className="text-center">
                                <td colSpan={5}>Nenhum histórico encontrado</td>
                              </tr>
                            )}{" "}
                          </>
                        )}
                      </tbody>
                    </CustomTable>
                  </Tab>
                )}
                <Tab eventKey="progress" title="Progresso">
                  {loading ? (
                    <Loading />
                  ) : (
                    <>
                      <BoxTimeLine>
                        {requestData?.progresso.length ? (
                          requestData?.progresso?.map((item, index) => (
                            <TimeLine key={index.toString()}>
                              <TimeLineContainer
                                content={item.ordem.toString()}
                                checked={item.concluido}
                              >
                                <TimeLineContent>
                                  <AssignmentIcon size={20} />
                                  <TimeLineContentBoxText>
                                    <TimeTitle>{item.atividade}</TimeTitle>
                                    <TimeSubTitle>{item.orgao}</TimeSubTitle>
                                  </TimeLineContentBoxText>
                                </TimeLineContent>
                              </TimeLineContainer>
                            </TimeLine>
                          ))
                        ) : (
                          <>
                            <h1>Nenhum progresso encontrado</h1>
                          </>
                        )}
                      </BoxTimeLine>
                    </>
                  )}
                </Tab>
                <Tab eventKey="docs_intern" title="Anexos internos">
                  <CustomTable striped responsive>
                    <thead className="table-header">
                      <tr>
                        <th>Ordem</th>
                        <th>Nome</th>
                        <th>Hash</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {loading ? (
                        <tr className="text-center">
                          <td colSpan={5}>
                            <Loading />
                          </td>
                        </tr>
                      ) : (
                        <>
                          {requestData?.anexos_internos.length ? (
                            requestData?.anexos_internos?.map(
                              // eslint-disable-next-line array-callback-return
                              (item, index) => {
                                const parts = item.arquivo.split("/");
                                const fileName = parts[parts.length - 1];
                                if (item.id) {
                                  return (
                                    <Fragment key={item.id}>
                                      <tr>
                                        <td>#{index + 1}</td>
                                        <td>{fileName}</td>
                                        <td>{item.id}</td>
                                        <td className="text-right">
                                          <Button
                                            variant="outline-secondary mx-2"
                                            onClick={() => {
                                              handleShowDocReceived({
                                                id: item.id,
                                                nome: fileName,
                                                url: item.arquivo,
                                              });
                                            }}
                                          >
                                            <MdVisibility size={22} />
                                          </Button>
                                        </td>
                                      </tr>
                                    </Fragment>
                                  );
                                }
                              }
                            )
                          ) : (
                            <tr className="text-center">
                              <td colSpan={5}>Nenhum histórico encontrado</td>
                            </tr>
                          )}{" "}
                        </>
                      )}
                    </tbody>
                  </CustomTable>
                </Tab>
                {requestData?.anexo_restituicao?.length > 0 && (
                  <Tab
                    eventKey="resititution-annex"
                    title="Anexos de restituição"
                  >
                    <CustomTable striped responsive>
                      <thead className="table-header">
                        <tr>
                          <th>Ordem</th>
                          <th className="text-end">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {requestData?.anexo_restituicao?.map((item, index) => (
                          <Fragment key={item.id}>
                            <tr>
                              <td>#{index + 1} - Anexo</td>
                              <td className="text-end">
                                <Button
                                  variant="outline-secondary mx-2"
                                  onClick={() => {
                                    handleShowDocReceived({
                                      id: `${item?.id}`,
                                      nome: `Anexo - ${index + 1}`,
                                      url: item?.arquivo,
                                    });
                                  }}
                                >
                                  <MdVisibility size={22} />
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => {
                                    showDetails && showDetails === item.id
                                      ? setShowDetails("")
                                      : setShowDetails(item.id);
                                  }}
                                >
                                  {showDetails && showDetails === item.id ? (
                                    <MdExpandMore />
                                  ) : (
                                    <MdExpandLess />
                                  )}
                                </Button>
                              </td>
                            </tr>
                            {showDetails && showDetails === item.id && (
                              <>
                                <tr className="hiddenDetails">
                                  <th colSpan={1}>Criado por:</th>
                                  <td className="text-right">
                                    {item?.criado_por || "Não informado"}
                                  </td>
                                </tr>
                              </>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                    </CustomTable>
                  </Tab>
                )}
              </Tabs>
            </BoxBodyCardTable>
            <CardFooterCustom>
              <ButtonReturn onClick={() => history.push("/solicitacoes")}>
                Voltar
              </ButtonReturn>
            </CardFooterCustom>
          </Card>
        </Col>
      </Row>
      <Modal
        show={showModalDocReceived}
        onHide={() => setShowModalDocReceived(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{docReceivedSelected?.nome}</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "750px",
          }}
        >
          {isImage(docReceivedSelected?.url) ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <img
                src={docReceivedSelected?.url}
                alt={docReceivedSelected?.nome}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
          ) : (
            <>
              {loadingAttachments && <Spinner />}
              <iframe
                title={docReceivedSelected?.nome}
                src={`https://docs.google.com/gview?url=${encodeURIComponent(
                  docReceivedSelected?.url
                )}&embedded=true`}
                style={{
                  width: "100%",
                  height: "750px",
                  border: "none",
                  display: loadingAttachments ? "none" : "block",
                }}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />
              <div style={{ textAlign: "center" }}>
                <p>
                  Se o documento não foi carregado automaticamente, clique no
                  botão abaixo para fazer o download.
                </p>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <a
            ref={downloadLink}
            href={docReceivedSelected?.url || "#"}
            download
            className="btn btn-primary"
            style={{ marginLeft: "10px" }}
          >
            Download
          </a>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showModalAnexos}
        onHide={() => setShowModalAnexos(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <object
            aria-label={anexosReceived?.id}
            data={anexosReceived?.arquivo || ""}
            style={{
              width: "100%",
              minHeight: 900,
            }}
          />
        </Modal.Body>
      </Modal>
    </ContainerMain>
  );
};

export default RequestDetails;
